/**
 * Centralized state management for authentication orchestrator
 */

import { reactive, readonly, computed, ref, watch, type ComputedRef } from 'vue';
import type { AuthPhase } from '@/types/profileTypes';
import type { ConditionalTrigger } from '@/types/auth-phases';
import { features } from '@/config/features';
import type { AccessPolicy } from '@/types/accessPolicy';
import type { ProgramJoinContext } from '@/types/programJoinContext';
import type { PortfolioUser, PortfolioCompletionStatus } from '../types';
import type {
  GeneralProfileData,
  TeacherProfileData,
  StudentProfileData,
  ParentProfileData,
  EmployerProfileData,
  SchoolProfileData,
  OrganizerProfileData
} from './useAuthProfile';

export interface AuthOrchestratorState {
  // UI State
  isOpen: boolean;
  phase: AuthPhase;
  previousPhase: AuthPhase;
  loading: boolean;
  error: string | null;

  // Auth Data
  contact: string;
  isEmail: boolean;
  isNewUser: boolean;
  hasPassword?: boolean;

  // Navigation
  originalRedirect: string | null;
  phaseHistory: AuthPhase[];
  completedPhases: Set<AuthPhase>;
  maxReachedPhase: AuthPhase;

  // Identity Linking
  pendingIdentityLink: {
    type: 'email' | 'phone' | null;
    value: string;
    otpSent: boolean;
    conflictingUserId?: string;
    conflictingUserData?: PortfolioUser;
    canMerge?: boolean;
    mergeIntended?: boolean;
  };

  // Account Discovery
  alternateContactSkipped: boolean;
  discoveredAccountId: string | null;
  discoveredAccountData: PortfolioUser | null;
  alternateContactValue: string;

  // Profile Data (uses local form types, not GraphQL Portfolio types)
  profileData: {
    general?: GeneralProfileData;
    teacher?: TeacherProfileData;
    student?: StudentProfileData;
    parent?: ParentProfileData;
    employer?: EmployerProfileData;
    school?: SchoolProfileData;
    organizer?: OrganizerProfileData;
  };
  selectedUserTypes: string[];
  currentRoleIndex: number;
  portfolioCompletionStatus: PortfolioCompletionStatus | null;

  // Recovery state
  recoveryAvailable: boolean;

  // Enhanced phase tracking
  encounteredPhases: Set<AuthPhase>;
  criticalDecisions: Map<string, unknown>;
  lockedPhases: Set<AuthPhase>;
  conditionalTriggers: Map<ConditionalTrigger, unknown>;

  // Navigation state tracking
  passedGates: Set<AuthPhase>;
  resolvedConflicts: Set<string>;

  // Express enroll mode: when 'express', the dialog renders a single
  // name+contact panel → inline OTP → silent enroll (no password/role/portfolio).
  // Set per-program (Program.expressEnroll) via ParticipantGuide. Default 'standard'.
  authMode: 'standard' | 'express';

  // Program access policy (adaptive sign-in ladder). When set (via the
  // public-program flow), the dialog renders its primary/secondary actions per
  // the floor/allowUpgrades values instead of the plain express/standard form.
  // null = no policy in effect (legacy behavior). See @/types/accessPolicy.
  accessPolicy: AccessPolicy | null;

  // The program the access-policy flow is enrolling into. Used by the
  // anonymous "Join now" (floor:'off') action so it can call enroll() without
  // re-deriving the id from the redirect URL. Empty when no policy flow active.
  accessPolicyProgramId: string;

  // What the visitor is joining (name, org, price, time, credential), shown on
  // the contact phase above the email box. null = plain "Enter PilotCity".
  programContext: ProgramJoinContext | null;

  // A contact typed on the landing itself (L15 arm D) that the contact phase submits on
  // open, so the visitor lands on the code step. null = the phase starts empty.
  contactPrefill: { value: string; isEmail: boolean } | null;

  // Internal flags
  showDialog?: boolean;

  // Non-dismissible mode: dialog cannot be closed by user until auth completes.
  // Used when an unauth visitor lands on a route that requires forcing them through auth
  // (e.g. /guide/{programId}/0 shared program link). Reset on completeAndRedirect.
  nondismissible: boolean;

  // Feature flags
  crossLinkingEnabled: boolean;
}

/**
 * Create the centralized auth state
 */
export function createAuthState() {
  // Initialize reactive state
  const state = reactive<AuthOrchestratorState>({
    // UI State
    isOpen: false,
    phase: 'idle',
    previousPhase: 'idle',
    loading: false,
    error: null,
    showDialog: false,

    // Auth Data
    contact: '',
    isEmail: true,
    isNewUser: false,
    hasPassword: false,

    // Navigation
    originalRedirect: null,
    phaseHistory: [],
    completedPhases: new Set(),
    maxReachedPhase: 'idle',

    // Identity Linking
    pendingIdentityLink: {
      type: null,
      value: '',
      otpSent: false
    },

    // Account Discovery
    alternateContactSkipped: false,
    discoveredAccountId: null,
    discoveredAccountData: null,
    alternateContactValue: '',

    // Profile Data
    profileData: {},
    selectedUserTypes: [],
    currentRoleIndex: 0,
    portfolioCompletionStatus: null,

    // Recovery
    recoveryAvailable: false,

    // Enhanced tracking
    encounteredPhases: new Set(),
    criticalDecisions: new Map(),
    lockedPhases: new Set(),
    conditionalTriggers: new Map(),

    // Navigation tracking
    passedGates: new Set(),
    resolvedConflicts: new Set(),

    // Non-dismissible mode (default off)
    nondismissible: false,

    // Express enroll mode (default standard)
    authMode: 'standard',

    // Access policy (default none — legacy behavior)
    accessPolicy: null,
    accessPolicyProgramId: '',
    programContext: null,
    contactPrefill: null,

    // Feature flags
    crossLinkingEnabled: features.accountCrossLinking
  });

  /**
   * Reset state to initial values
   */
  const resetState = () => {
    Object.assign(state, {
      isOpen: false,
      phase: 'idle',
      previousPhase: 'idle',
      loading: false,
      error: null,
      contact: '',
      isEmail: true,
      isNewUser: false,
      hasPassword: false,
      originalRedirect: null,
      phaseHistory: [],
      completedPhases: new Set(),
      maxReachedPhase: 'idle',
      pendingIdentityLink: {
        type: null,
        value: '',
        otpSent: false
      },
      alternateContactSkipped: false,
      discoveredAccountId: null,
      discoveredAccountData: null,
      alternateContactValue: '',
      selectedUserTypes: [],
      currentRoleIndex: 0,
      profileData: {},
      portfolioCompletionStatus: null,
      recoveryAvailable: false,
      encounteredPhases: new Set(),
      criticalDecisions: new Map(),
      lockedPhases: new Set(),
      conditionalTriggers: new Map(),
      passedGates: new Set(),
      resolvedConflicts: new Set(),
      nondismissible: false,
      authMode: 'standard',
      accessPolicy: null,
      accessPolicyProgramId: '',
      programContext: null,
      contactPrefill: null,
      crossLinkingEnabled: features.accountCrossLinking
    });
  };

  /**
   * Current role being processed
   */
  const currentRole = computed(() => {
    if (state.selectedUserTypes.length === 0) return null;
    if (state.currentRoleIndex >= state.selectedUserTypes.length) return null;
    return state.selectedUserTypes[state.currentRoleIndex];
  });

  /**
   * Progress through the auth flow
   */
  const progress = computed(() => {
    const phases: AuthPhase[] = [
      'contact',
      'otp',
      'password',
      'setPassword',
      'alternateContact',
      'generalProfile'
    ];

    // Add role-specific phases
    if (state.selectedUserTypes.length > 0) {
      state.selectedUserTypes.forEach(type => {
        phases.push(`${type.toLowerCase()}Profile` as AuthPhase);
      });
    }

    const currentIndex = phases.indexOf(state.phase);
    if (currentIndex === -1) return 0;

    return Math.round(((currentIndex + 1) / phases.length) * 100);
  });

  /**
   * Clear any error state
   */
  const clearError = () => {
    state.error = null;
  };

  // Create a computed ref that directly accesses the internal state
  // This ensures proper reactivity in Vue 2.7 with Vuetify 2 dialogs
  const isOpen = computed({
    get: () => state.isOpen,
    set: (value: boolean) => {
      state.isOpen = value;
    }
  });

  return {
    // Expose readonly state for external consumption
    state: readonly(state),
    // Expose mutable state for internal modules
    internalState: state,
    // Expose isOpen separately for v-dialog compatibility
    isOpen,
    // Utility functions
    resetState,
    clearError,
    // Computed properties
    currentRole,
    progress
  };
}
